import { useEffect } from 'react';
import { CodJourney } from 'src/constants/assemblyJourneys';
import { useEvent } from 'src/hooks/useEvent';
import { DynamicSectionEvents } from 'src/interfaces';
import { GcfMockData } from 'src/utils/gcf-helper';

export const useCodEvents = (
  customerState: Partial<GcfMockData>,
  activeAssemblyJourney?: CodJourney,
  selectedDaypartId?: number,
) => {
  const { triggerEvent } = useEvent(DynamicSectionEvents.COD_ORDER_OPEN, {});
  const { triggerEvent: triggerDYSEvent } = useEvent(
    GcfMockData.DY_SUGGESTIONS_OPEN,
    {},
  );
  const { triggerEvent: triggerDYREvent } = useEvent(
    GcfMockData.DY_RECOMMENDATIONS_UPDATE,
    {},
  );
  const { triggerEvent: triggerThankyouEvent } = useEvent(
    DynamicSectionEvents.COD_THANK_YOU_OPEN,
    {},
  );

  useEffect(
    () => {
      if (!activeAssemblyJourney) {
        return;
      }

      const isCustomerMop =
        customerState === GcfMockData.COD_CUSTOMER_KNOWN_MOP;
      const isCustomerKnown =
        customerState === GcfMockData.COD_CUSTOMER_KNOWN || isCustomerMop;

      const delay = selectedDaypartId !== undefined ? 800 : 100;

      const wait = (ms: number) =>
        new Promise<void>((resolve) => setTimeout(resolve, ms));

      const dispatchEvents = () => {
        triggerEvent({
          orderType: customerState,
        });

        triggerThankyouEvent({
          orderType: customerState,
          codData: {
            isCustomerKnown,
            isMop: isCustomerMop,
          },
        });

        triggerDYREvent({
          orderType: customerState,
          isCustomerKnown,
          isMop: isCustomerMop,
        });

        setTimeout(() => {
          triggerDYSEvent({
            orderType: customerState,
            isCustomerKnown,
            isMop: isCustomerMop,
          });
        }, 50);
      };

      /**
       * Use Promise chain with delays to ensure events are dispatched after React has
       * finished rendering and components have mounted their event listeners.
       */
      Promise.resolve()
        .then(() => wait(0))
        .then(() => wait(delay))
        .then(() => {
          dispatchEvents();

          if (selectedDaypartId !== undefined) {
            return wait(200).then(() => dispatchEvents());
          }
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customerState, activeAssemblyJourney, selectedDaypartId],
  );
};
